import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { get } from "../../lib/api";
import { timestamp } from "../../lib/format";
import {
  answerHeadline,
  attemptDuration,
  attemptOutcomeLabel,
  attemptTone,
  prettyBody,
  prettyJson,
} from "../../lib/sessionPushes";
import type { SessionPushAttemptDetail } from "../../lib/types";
import { CopyButton } from "../ui/Copy";
import { ErrorNotice } from "../ui/Feedback";
import { Modal } from "../ui/Modal";
import { Pill } from "../ui/Status";
import { Datum, MicroLabel } from "../ui/Text";

/**
 * One push request, over the log it was clicked from: what we sent, and what the
 * partner said back, each exactly as it went over the wire.
 *
 * The request is shown without its Authorization header because the server never
 * kept it; the line says so rather than leaving the header to look forgotten.
 */
export function PushAttemptView({
  attemptId,
  timeZone,
  open,
  onOpenChange,
}: {
  readonly attemptId: string;
  readonly timeZone: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}) {
  // Held through the exit animation, as `SessionView` does.
  const [shownId, setShownId] = useState(attemptId);
  if (attemptId !== "" && attemptId !== shownId) setShownId(attemptId);

  const attempt = useQuery({
    queryKey: ["session-push", shownId],
    queryFn: () => get<SessionPushAttemptDetail>(`/session-pushes/${encodeURIComponent(shownId)}`),
    enabled: open && shownId !== "",
    // A request that was made never changes.
    staleTime: Number.POSITIVE_INFINITY,
  });
  const row = attempt.data;

  return (
    <Modal
      size="wide"
      open={open}
      onOpenChange={onOpenChange}
      eyebrow={row === undefined ? "Push request" : `Push request · ${row.app.name}`}
      title={
        row === undefined
          ? "Push request"
          : `${row.session.chargePointId} · ${row.plate ?? "no plate"}`
      }
      status={
        row === undefined ? undefined : (
          <Pill tone={attemptTone(row.outcome)}>{attemptOutcomeLabel(row.outcome)}</Pill>
        )
      }
    >
      {attempt.isError ? (
        <ErrorNotice error={attempt.error} onRetry={() => void attempt.refetch()} />
      ) : row === undefined ? (
        <MicroLabel>Loading</MicroLabel>
      ) : (
        <div className="flex flex-col gap-6">
          <dl className="m-0 grid grid-cols-2 gap-4 md:grid-cols-4">
            <Datum label="Sent">{timestamp(row.attemptedAt, timeZone)}</Datum>
            <Datum label="Answer">{answerHeadline(row)}</Datum>
            <Datum label="Took">{attemptDuration(row.durationMs)}</Datum>
            <Datum label="Session">
              <Link
                to={`/sessions?session=${encodeURIComponent(row.session.id)}`}
                className="text-signal underline decoration-1 underline-offset-4 hover:no-underline"
              >
                Open session
              </Link>
            </Datum>
          </dl>

          <Exchange
            heading="Request"
            line={`POST ${row.url}`}
            note="Authorization: Bearer … — the token is never kept."
            body={prettyJson(row.requestBody)}
            what="request body"
          />
          <Exchange
            heading="Response"
            line={answerHeadline(row)}
            body={prettyBody(row.responseBody)}
            what="response body"
          />
        </div>
      )}
    </Modal>
  );
}

function Exchange({
  heading,
  line,
  note,
  body,
  what,
}: {
  readonly heading: string;
  readonly line: string;
  readonly note?: string;
  readonly body: string;
  readonly what: string;
}) {
  return (
    <section aria-label={heading} className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <MicroLabel>{heading}</MicroLabel>
        <CopyButton value={body} what={what} variant="ghost" />
      </div>
      <p className="m-0 break-all font-mono text-data text-ink">{line}</p>
      {note === undefined ? null : (
        <p className="m-0 font-mono text-micro tracking-normal normal-case text-ink-faint">
          {note}
        </p>
      )}
      <pre className="m-0 max-h-96 overflow-auto border border-rule bg-paper-sunken px-4 py-3 font-mono text-micro tracking-normal normal-case whitespace-pre-wrap break-all text-ink">
        {body}
      </pre>
    </section>
  );
}
